/**
 * Маленькая иконка уверенности OSINT-данных рядом со значением
 * телефона/email. По образцу исходного проекта (ContactsSection.tsx:1157):
 * крошечный визуальный индикатор + tooltip с описанием.
 *
 * UNDEFINED не рисуется (как в исходном проекте — `showPhoneConfidence` отсекает).
 */

import React from "react";
import { ShieldCheck, Shield, ShieldAlert } from "lucide-react";

import type { OsintLookup } from "@/types/osint";
import { cn } from "@/utils";
import { computeOsintConfidence, describeOsintConfidence, type OsintConfidence } from "@/utils/osintConfidence";

interface OsintConfidenceIconProps {
  lookup: OsintLookup | null | undefined;
  /** Не рендерить, пока обогащение ещё идёт (чтобы не «мигало» LOW→HIGH) */
  isEnriching?: boolean;
  className?: string;
}

const TONE: Record<Exclude<OsintConfidence, "UNDEFINED">, { Icon: typeof Shield; cls: string }> = {
  HIGH: {
    Icon: ShieldCheck,
    cls: "text-success-600 dark:text-success-400",
  },
  MEDIUM: {
    Icon: Shield,
    cls: "text-warning-600 dark:text-warning-400",
  },
  LOW: {
    Icon: ShieldAlert,
    cls: "text-error-500 dark:text-error-400",
  },
};

const OsintConfidenceIcon: React.FC<OsintConfidenceIconProps> = ({ lookup, isEnriching, className }) => {
  if (isEnriching) return null;
  const level = computeOsintConfidence(lookup);
  if (level === "UNDEFINED") return null;

  const { Icon, cls } = TONE[level];
  const title = describeOsintConfidence(level, lookup);

  return (
    <span className={cn("inline-flex items-center", className)} title={title} aria-label={title} role="img">
      <Icon className={cn("w-3.5 h-3.5", cls)} strokeWidth={2.25} />
    </span>
  );
};

export default OsintConfidenceIcon;
